import { parseServerTime } from './serverTime';

// The landing and ground track drawn when a row is clicked on the arrivals, Departures and
// On Ground tabs: AircraftTrackingService.getTrackPoints by hex over a time window (any receiver),
// trimmed to start 2 km along the track before touchdown and run to the last point.

export const PRE_TOUCHDOWN_M = 2000;
// At or below this barometric altitude (or with no altitude, which is how surface positions
// arrive) a point counts as on the ground. On a 3° approach 300 ft is the last ~300 m before the
// threshold at a sea-level airport; higher airports never report it airborne and fall to the
// no-altitude test.
export const GROUND_ALT_FT = 300;

const TEN_MIN_MS = 10 * 60 * 1000;
const THREE_H_MS = 3 * 60 * 60 * 1000;

const hasPosition = p => p && Number.isFinite(p.latitude) && Number.isFinite(p.longitude);
const isGround = p => p.altitude == null || p.altitude <= GROUND_ALT_FT;
const pointMs = p => {
  if (p.timestamp == null) return null;
  const d = typeof p.timestamp === 'string' ? parseServerTime(p.timestamp) : new Date(p.timestamp);
  return d && !isNaN(d) ? d.getTime() : null;
};
const toMs = t => {
  if (t == null) return null;
  if (typeof t === 'number') return t;
  const d = parseServerTime(t);
  return d && !isNaN(d) ? d.getTime() : null;
};

export function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Index of the touchdown point: the first on-ground point straight after an airborne one (the
 * last such when `last`, for a long window that may hold earlier landings). Without that
 * transition, the first point at or after `touchdownAt`. -1 when neither is found (still in the
 * air, or the window starts on the ground with no landing time).
 */
export function findTouchdownIndex(points, { touchdownAt = null, last = false } = {}) {
  let found = -1;
  for (let i = 1; i < points.length; i++) {
    if (!isGround(points[i - 1]) && isGround(points[i])) {
      found = i;
      if (!last) break;
    }
  }
  if (found >= 0) return found;
  const tdMs = toMs(touchdownAt);
  if (tdMs != null) {
    const idx = points.findIndex(p => { const t = pointMs(p); return t != null && t >= tdMs; });
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * [lat, lon] positions from PRE_TOUCHDOWN_M along the track before touchdown to the end. The
 * start is interpolated on the segment that crosses 2 km, because the stored track is simplified
 * (about one point a minute, ~4 km on final). With no touchdown found the whole track is returned.
 */
export function trimToLanding(points, opts = {}) {
  const pts = (points || []).filter(hasPosition);
  const ll = p => [p.latitude, p.longitude];
  const td = findTouchdownIndex(pts, opts);
  if (td < 0) return pts.map(ll);
  let acc = 0;
  for (let i = td; i > 0; i--) {
    const a = pts[i];
    const b = pts[i - 1];
    const seg = haversineM(a.latitude, a.longitude, b.latitude, b.longitude);
    if (acc + seg >= PRE_TOUCHDOWN_M) {
      const f = seg > 0 ? (PRE_TOUCHDOWN_M - acc) / seg : 0;
      const start = [a.latitude + (b.latitude - a.latitude) * f, a.longitude + (b.longitude - a.longitude) * f];
      return [start, ...pts.slice(i).map(ll)];
    }
    acc += seg;
  }
  return pts.map(ll);
}

/** Epoch ms as the server's LocalDateTime (UTC, no zone), the format it sends and parses. */
export function toServerDateTime(ms) {
  return new Date(ms).toISOString().slice(0, 19);
}

function query(icao24, callsign, startMs, touchdownAt, last) {
  if (!icao24) return null;
  const startTime = startMs != null ? toServerDateTime(startMs) : null;
  return {
    key: `${icao24}|${callsign || ''}|${startTime || ''}`,
    icao24, callsign: callsign || null, startTime, touchdownAt, last,
  };
}

/**
 * Arrivals: from 10 min before the landing (actual, or the ETA of a presumed landing) to now.
 * In the air: from the first prediction, so the approach shows; the server caps the span at 6 h.
 */
export function arrivalTrackQuery(row) {
  if (!row) return null;
  const landed = row.actualLandedAt || (row._presumedLanded ? row.lastEtaTime : null);
  const landedMs = toMs(landed);
  if (landedMs != null) return query(row.icaoAddress, row.callsign, landedMs - TEN_MIN_MS, landed, false);
  return query(row.icaoAddress, row.callsign, toMs(row.firstPredictionAt), null, false);
}

/** On Ground: from 10 min before arrivedAt. Without it the server's last 30 min, last touchdown. */
export function onGroundTrackQuery(row) {
  if (!row) return null;
  const arrivedMs = toMs(row.arrivedAt);
  if (arrivedMs != null) return query(row.icaoAddress, row.callsign, arrivedMs - TEN_MIN_MS, row.arrivedAt, false);
  return query(row.icaoAddress, row.callsign, null, null, true);
}

/**
 * Departures: from 10 min before the arrival that preceded the session, taken from the On Ground
 * feed's arrivedAt for the same hex. Unknown: from 3 h before the session start, trimmed to the
 * last touchdown in the points.
 */
export function departureTrackQuery(row, groundAircraft = []) {
  if (!row) return null;
  const sessionMs = toMs(row.sessionStartAt);
  const hex = (row.icaoAddress || '').toUpperCase();
  let arrivedAt = null;
  for (const g of groundAircraft || []) {
    if ((g.icaoAddress || '').toUpperCase() !== hex) continue;
    const a = toMs(g.arrivedAt);
    if (a == null || (sessionMs != null && a > sessionMs)) continue;
    if (arrivedAt == null || a > toMs(arrivedAt)) arrivedAt = g.arrivedAt;
  }
  if (arrivedAt) return query(row.icaoAddress, row.callsign, toMs(arrivedAt) - TEN_MIN_MS, arrivedAt, false);
  return query(row.icaoAddress, row.callsign, sessionMs != null ? sessionMs - THREE_H_MS : null, null, true);
}
